/** @jsxImportSource @emotion/react */
import { ArcherContainer, ArcherElement } from "react-archer";

// Lienzo de un flujograma de pasos, con las flechas dibujadas por
// react-archer. Lo usan la configuración del flujo de un tipo de actividad y
// la vista de una actividad: cada una decide qué va dentro de la caja de cada
// paso; la ubicación de las cajas y las flechas es la misma.
//
// Las cajas son elementos comunes de la página (no hay lienzo que se arrastre),
// así que sus botones y links funcionan como en cualquier otro lado. Se
// ubican en una grilla: una fila por nivel (a cuántas aprobaciones está el
// paso del inicial), centrada y con las cajas alineadas arriba, y un carril a
// cada costado por donde pasan los saltos y las vueltas atrás.
//
// Aprobar se dibuja en verde y rechazar en rojo punteado. Lo que va a la fila
// de abajo baja recto; los saltos y las vueltas atrás pasan por un punto del
// carril (aprobar por la derecha, rechazar por la izquierda), y "se queda" es
// un lazo debajo del paso. Por eso "Fin sin cumplimiento" va a la izquierda y
// "Fin" a la derecha.

const SEPARACION = 36;
const SEPARACION_VERTICAL = 56;
const ANCHO_CARRIL = 95;
const ANCHO_FIN = 76;
const ANCHO_FIN_SIN_CUMPLIMIENTO = 170;
const ALTO_FIN = 34;

// react-archer pinta la punta de la flecha con un atributo, donde no funcionan
// las variables de CSS: se lee el color real
function colorDe(variable, porDefecto) {
  if (typeof window === "undefined") return porDefecto;
  const valor = getComputedStyle(document.documentElement)
    .getPropertyValue(variable)
    .trim();
  return valor || porDefecto;
}

const FIN = "fin";
const FIN_SIN_CUMPLIMIENTO = "fin_sin_cumplimiento";
const finalDe = (transicion) =>
  transicion.sinCumplimiento ? FIN_SIN_CUMPLIMIENTO : FIN;

// En este orden, de izquierda a derecha
const FINALES = [
  {
    clave: FIN_SIN_CUMPLIMIENTO,
    texto: "Fin sin cumplimiento",
    ancho: ANCHO_FIN_SIN_CUMPLIMIENTO
  },
  { clave: FIN, texto: "Fin", ancho: ANCHO_FIN }
];

const idPaso = (id) => `paso-${id}`;
const idFinal = (clave) => `final-${clave}`;
const idDestino = (t) =>
  t.destinoId === null ? idFinal(finalDe(t)) : idPaso(t.destinoId);
const claveDe = (t) =>
  `${t.origenId}-${t.resultado}-${t.destinoId ?? finalDe(t)}`;

// Nivel de cada paso: distancia al paso inicial siguiendo aprobaciones. A los
// que solo se llega rechazando se los ubica siguiendo cualquier transición, y
// a los que no se llega de ninguna forma (los que están sin conectar), al
// final.
function calcularNiveles(pasos, transiciones, inicialId) {
  const nivel = new Map();
  const recorrer = (soloAprobar) => {
    const cola = [...nivel.keys()];
    while (cola.length) {
      const actual = cola.shift();
      for (const t of transiciones) {
        if (t.origenId !== actual || t.destinoId === null) continue;
        if (soloAprobar && t.resultado !== "APROBADO") continue;
        if (nivel.has(t.destinoId)) continue;
        nivel.set(t.destinoId, nivel.get(actual) + 1);
        cola.push(t.destinoId);
      }
    }
  };
  nivel.set(inicialId, 0);
  recorrer(true);
  recorrer(false);
  let ultimo = Math.max(...nivel.values());
  for (const paso of pasos) {
    if (!nivel.has(paso.id)) nivel.set(paso.id, ++ultimo);
  }
  return nivel;
}

// Lazo de "se queda en el mismo paso", debajo de la caja
function Lazo({ aprueba, color }) {
  const lado = aprueba ? 1 : -1;
  return (
    <svg
      width="70"
      height="40"
      css={{
        position: "absolute",
        bottom: "-38px",
        [aprueba ? "right" : "left"]: "6px",
        overflow: "visible",
        pointerEvents: "none"
      }}
    >
      <path
        d={`M ${35 - lado * 12} 0 C ${35 - lado * 30} 34, ${35 + lado * 30} 34, ${
          35 + lado * 11
        } 3`}
        fill="none"
        strokeWidth="1.5"
        strokeDasharray={aprueba ? undefined : "5 4"}
        style={{ stroke: color }}
      />
      <path
        d={`M ${35 + lado * 11} 1 l ${-lado * 2} 7 l ${lado * 6} -3 z`}
        style={{ fill: color }}
      />
    </svg>
  );
}

/**
 * @param {{id: number}[]} pasos en orden
 * @param {{origenId: number, resultado: "APROBADO"|"RECHAZADO", destinoId: number|null, sinCumplimiento?: boolean}[]} transiciones
 *   destinoId null es un final de la actividad; con sinCumplimiento, el fin sin cumplimiento
 * @param {number|null} pasoInicialId paso en el que empieza la actividad
 * @param {number} anchoPaso ancho de la caja de un paso, en px
 * @param {(paso) => number} altoDePaso alto de la caja de un paso, en px
 * @param {(paso) => object} [estiloDePaso] estilos extra para la caja de un paso
 * @param {(paso) => React.ReactNode} renderPaso contenido de la caja de un paso
 */
export default function Flujograma({
  pasos,
  transiciones,
  pasoInicialId,
  anchoPaso,
  altoDePaso,
  estiloDePaso,
  renderPaso
}) {
  if (!pasos?.length) return null;

  const verde = colorDe("--verde", "#22c55e");
  const rojo = colorDe("--rojo", "#cd2735");

  const idsValidos = new Set(pasos.map((p) => p.id));
  const validas = (transiciones || []).filter(
    (t) =>
      idsValidos.has(t.origenId) &&
      (t.destinoId === null || idsValidos.has(t.destinoId))
  );
  const inicialId = idsValidos.has(pasoInicialId) ? pasoInicialId : pasos[0].id;
  const nivel = calcularNiveles(pasos, validas, inicialId);

  // Filas: los pasos de cada nivel, en su orden
  const filas = [];
  for (const paso of pasos) {
    (filas[nivel.get(paso.id)] ||= []).push(paso);
  }
  const filasConPasos = filas.filter(Boolean);
  // Nivel ya compactado (sin huecos) de cada paso, y los finales al final
  const fila = new Map();
  filasConPasos.forEach((ps, n) =>
    ps.forEach((p) => fila.set(idPaso(p.id), n))
  );
  const finales = FINALES.filter(({ clave }) =>
    validas.some((t) => t.destinoId === null && finalDe(t) === clave)
  );
  finales.forEach((f) => fila.set(idFinal(f.clave), filasConPasos.length));

  // Clasifica cada flecha: lazo, recta a la fila de abajo, o por el carril
  const relaciones = new Map(); // id de origen -> relaciones de react-archer
  const agregar = (origen, relacion) => {
    if (!relaciones.has(origen)) relaciones.set(origen, []);
    relaciones.get(origen).push(relacion);
  };
  const lazos = new Map(); // id de paso -> [aprueba, ...]
  const porCarril = [];

  for (const t of validas) {
    const aprueba = t.resultado === "APROBADO";
    const origen = idPaso(t.origenId);
    const destino = idDestino(t);
    const estilo = {
      strokeColor: aprueba ? verde : rojo,
      strokeWidth: 1.5,
      strokeDasharray: aprueba ? undefined : "5 4"
    };

    if (origen === destino) {
      lazos.set(origen, [...(lazos.get(origen) || []), aprueba]);
      continue;
    }

    // A la fila de abajo baja recto, salvo un rechazo que va al mismo paso
    // que una aprobación: ese va por el carril, para no pisarse
    const aLaDeAbajo = fila.get(destino) === fila.get(origen) + 1;
    const pisaAprobacion =
      !aprueba &&
      validas.some(
        (o) =>
          o !== t &&
          o.resultado === "APROBADO" &&
          o.origenId === t.origenId &&
          idDestino(o) === destino
      );
    if (aLaDeAbajo && !pisaAprobacion) {
      agregar(origen, {
        targetId: destino,
        sourceAnchor: "bottom",
        targetAnchor: "top",
        style: estilo
      });
      continue;
    }

    porCarril.push({ t, aprueba, origen, destino, estilo });
  }

  // Puntos de paso en los carriles. En cada carril, cuanto más lejos va la
  // flecha, más afuera pasa, para que los arcos no se encimen.
  const puntos = [];
  for (const aprueba of [true, false]) {
    porCarril
      .filter((f) => f.aprueba === aprueba)
      .map((f) => ({
        ...f,
        desde: fila.get(f.origen),
        hasta: fila.get(f.destino)
      }))
      .sort(
        (a, b) => Math.abs(a.hasta - a.desde) - Math.abs(b.hasta - b.desde)
      )
      .forEach((f, k) => {
        const lado = aprueba ? "right" : "left";
        const haciaAbajo = f.hasta >= f.desde;
        const idPunto = `punto-${claveDe(f.t)}`;
        const bajo = Math.min(f.desde, f.hasta);
        const alto = Math.max(f.desde, f.hasta);
        const medio = (bajo + alto) / 2;
        // Fila de la grilla donde va el punto: la del medio, o el hueco entre
        // dos filas si el medio cae entre ellas
        const enHueco = !Number.isInteger(medio) || bajo === alto;
        puntos.push({
          id: idPunto,
          lado,
          filaGrilla: Math.floor(medio) + 1,
          enHueco,
          corrimiento: Math.min(14 + k * 14, ANCHO_CARRIL - 8)
        });
        // Del paso al punto, sin punta
        agregar(f.origen, {
          targetId: idPunto,
          sourceAnchor: lado,
          targetAnchor: haciaAbajo ? "top" : "bottom",
          style: { ...f.estilo, endMarker: false }
        });
        // Del punto al destino
        agregar(idPunto, {
          targetId: f.destino,
          sourceAnchor: haciaAbajo ? "bottom" : "top",
          targetAnchor: lado,
          style: f.estilo
        });
      });
  }

  const filaDeGrilla = (n) => ({
    gridColumn: 2,
    gridRow: n + 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    gap: `${SEPARACION}px`
  });

  return (
    <div>
      {/* Si el flujo es más ancho que su contenedor, se desplaza hacia los
          costados */}
      <div css={{ overflowX: "auto" }}>
        <ArcherContainer
          // Si cambia la estructura del flujo (p. ej. editando la
          // configuración), las flechas se recalculan desde cero
          key={JSON.stringify([
            pasos.map((p) => [p.id, altoDePaso(p)]),
            validas.map(claveDe)
          ])}
          strokeColor={verde}
          strokeWidth={1.5}
          lineStyle="curve"
          endShape={{ arrow: { arrowLength: 5, arrowThickness: 5 } }}
          style={{ width: "max-content", margin: "0 auto" }}
          // Las flechas van debajo de las cajas
          svgContainerStyle={{ zIndex: 0 }}
        >
          <div
            css={{
              display: "grid",
              gridTemplateColumns: `${ANCHO_CARRIL}px max-content ${ANCHO_CARRIL}px`,
              rowGap: `${SEPARACION_VERTICAL}px`,
              padding: "12px 0 24px"
            }}
          >
            {filasConPasos.map((ps, n) => (
              <div key={n} css={filaDeGrilla(n)}>
                {ps.map((paso) => {
                  const id = idPaso(paso.id);
                  return (
                    <ArcherElement
                      key={paso.id}
                      id={id}
                      relations={relaciones.get(id) || []}
                    >
                      <div
                        css={{
                          position: "relative",
                          zIndex: 1,
                          width: `${anchoPaso}px`,
                          // Alto mínimo: si el contenido no entra (p. ej.
                          // un nombre largo en dos líneas), la caja crece
                          minHeight: `${altoDePaso(paso)}px`,
                          boxSizing: "border-box",
                          backgroundColor: "white",
                          border: "1px solid var(--grisTextos)",
                          borderRadius: "8px",
                          fontSize: "0.8rem",
                          lineHeight: 1.35,
                          ...(estiloDePaso ? estiloDePaso(paso) : {})
                        }}
                      >
                        {renderPaso(paso)}
                        {(lazos.get(id) || []).map((aprueba) => (
                          <Lazo
                            key={aprueba ? "aprueba" : "rechaza"}
                            aprueba={aprueba}
                            color={aprueba ? verde : rojo}
                          />
                        ))}
                      </div>
                    </ArcherElement>
                  );
                })}
              </div>
            ))}

            {finales.length > 0 && (
              <div css={filaDeGrilla(filasConPasos.length)}>
                {finales.map((final) => (
                  <ArcherElement key={final.clave} id={idFinal(final.clave)}>
                    <div
                      css={{
                        position: "relative",
                        zIndex: 1,
                        width: `${final.ancho}px`,
                        height: `${ALTO_FIN}px`,
                        boxSizing: "border-box",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "var(--surface-200)",
                        border: "1px solid var(--grisTextos)",
                        borderRadius: `${ALTO_FIN / 2}px`,
                        fontSize: "0.8rem"
                      }}
                    >
                      {final.texto}
                    </div>
                  </ArcherElement>
                ))}
              </div>
            )}

            {/* Puntos de paso de los saltos y las vueltas atrás, en los
                carriles de los costados */}
            {puntos.map((punto) => (
              <ArcherElement
                key={punto.id}
                id={punto.id}
                relations={relaciones.get(punto.id) || []}
              >
                <div
                  css={{
                    gridColumn: punto.lado === "right" ? 3 : 1,
                    gridRow: punto.filaGrilla,
                    alignSelf: punto.enHueco ? "end" : "center",
                    justifySelf: punto.lado === "right" ? "start" : "end",
                    width: "1px",
                    height: "1px",
                    [punto.lado === "right" ? "marginLeft" : "marginRight"]: `${punto.corrimiento}px`,
                    // En el hueco entre dos filas: medio hueco más abajo
                    transform: punto.enHueco
                      ? `translateY(${SEPARACION_VERTICAL / 2}px)`
                      : undefined
                  }}
                />
              </ArcherElement>
            ))}
          </div>
        </ArcherContainer>
      </div>

      <div
        css={{
          display: "flex",
          justifyContent: "center",
          gap: "1.5em",
          fontSize: "0.8rem",
          color: "var(--grisTextos)",
          marginTop: "0.5em"
        }}
      >
        <span>
          <span css={{ color: "var(--verde)", fontWeight: 700 }}>——</span>{" "}
          Aprueba
        </span>
        <span>
          <span css={{ color: "var(--rojo)", fontWeight: 700 }}>- - -</span>{" "}
          Rechaza
        </span>
      </div>
    </div>
  );
}
